import React from "react";

/**
 * CinematicDuck — custom hand-drawn SVG duck used across the app
 * (splash screen, slow-down easter egg, etc.).
 *
 * Every rig part (wing, head, beak, legs, tail, eye glint) is a grouped
 * <g> with a transform-origin declared in the co-located <style> block.
 * All transforms are GPU-accelerated + hardware-composited.
 *
 * The outer wrapper of the caller controls the horizontal chase (translate).
 * This component only animates the duck's *internal* rigging.
 */
export default function CinematicDuck({ className = "" }) {
  return (
    <>
      <svg
        className={`duck-svg ${className}`}
        viewBox="0 0 96 96"
        xmlns="http://www.w3.org/2000/svg"
        role="img"
        aria-label="Credit Quack duck"
      >
        <defs>
          <radialGradient id="cq-body-grad" cx="0.38" cy="0.32" r="0.85">
            <stop offset="0%" stopColor="#fef9c3" />
            <stop offset="35%" stopColor="#fde047" />
            <stop offset="72%" stopColor="#f59e0b" />
            <stop offset="100%" stopColor="#b45309" />
          </radialGradient>
          <radialGradient id="cq-head-grad" cx="0.4" cy="0.35" r="0.75">
            <stop offset="0%" stopColor="#fef9c3" />
            <stop offset="45%" stopColor="#fcd34d" />
            <stop offset="100%" stopColor="#d97706" />
          </radialGradient>
          <linearGradient id="cq-belly-grad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#fffbeb" stopOpacity="0.9" />
            <stop offset="100%" stopColor="#fef3c7" stopOpacity="0" />
          </linearGradient>
          <linearGradient id="cq-beak-grad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#fb923c" />
            <stop offset="60%" stopColor="#f97316" />
            <stop offset="100%" stopColor="#c2410c" />
          </linearGradient>
          <linearGradient id="cq-wing-grad" x1="0" y1="0" x2="0.6" y2="1">
            <stop offset="0%" stopColor="#fbbf24" />
            <stop offset="70%" stopColor="#d97706" />
            <stop offset="100%" stopColor="#92400e" />
          </linearGradient>
          <linearGradient id="cq-leg-grad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#f97316" />
            <stop offset="100%" stopColor="#9a3412" />
          </linearGradient>
        </defs>

        {/* Tail feathers */}
        <g className="duck-tail">
          <path d="M28 62 Q16 58 12 66 Q18 68 22 70 Q18 74 20 78 Q26 74 30 72 Z"
            fill="url(#cq-body-grad)" stroke="#78350f" strokeWidth="1" strokeLinejoin="round" />
          <path d="M22 66 Q16 66 14 70 Q18 71 22 72 Z" fill="#b45309" opacity="0.5" />
        </g>
        {/* Body */}
        <g>
          <ellipse cx="46" cy="62" rx="26" ry="20" fill="url(#cq-body-grad)" stroke="#78350f" strokeWidth="1.2" />
          <ellipse cx="46" cy="70" rx="18" ry="8" fill="url(#cq-belly-grad)" />
        </g>
        {/* Wing — secondary lagged feather */}
        <g className="duck-wing-feather">
          <path d="M40 54 Q56 52 64 66 Q60 72 52 74 Q42 72 38 66 Z" fill="#b45309" opacity="0.55" />
        </g>
        {/* Wing — primary */}
        <g className="duck-wing">
          <path d="M40 54 Q58 50 66 64 Q62 72 52 74 Q42 72 38 64 Z"
            fill="url(#cq-wing-grad)" stroke="#78350f" strokeWidth="1" strokeLinejoin="round" />
          <path d="M50 58 Q56 60 60 68" stroke="#78350f" strokeWidth="0.6" fill="none" opacity="0.6" />
          <path d="M46 60 Q52 62 56 70" stroke="#78350f" strokeWidth="0.6" fill="none" opacity="0.5" />
          <path d="M43 63 Q48 65 52 72" stroke="#78350f" strokeWidth="0.6" fill="none" opacity="0.4" />
          <path d="M42 55 Q55 52 62 60" stroke="#fef3c7" strokeWidth="0.9" fill="none" opacity="0.7" strokeLinecap="round" />
        </g>
        {/* Legs */}
        <g className="duck-leg-back">
          <rect x="56" y="76" width="4" height="12" rx="2" fill="url(#cq-leg-grad)" stroke="#7c2d12" strokeWidth="0.6" />
          <path d="M52 86 Q58 84 64 86 Q64 90 58 90 Q52 90 52 86 Z" fill="url(#cq-leg-grad)" stroke="#7c2d12" strokeWidth="0.6" />
        </g>
        <g className="duck-leg-front">
          <rect x="44" y="76" width="4" height="12" rx="2" fill="#fb923c" stroke="#7c2d12" strokeWidth="0.6" />
          <path d="M40 86 Q46 84 52 86 Q52 90 46 90 Q40 90 40 86 Z" fill="#fb923c" stroke="#7c2d12" strokeWidth="0.6" />
        </g>
        {/* Head + Beak */}
        <g className="duck-head">
          <circle cx="62" cy="44" r="16" fill="url(#cq-head-grad)" stroke="#78350f" strokeWidth="1.2" />
          <ellipse cx="58" cy="50" rx="4" ry="2" fill="#f97316" opacity="0.18" />
          <path d="M74 46 Q86 46 84 52 Q78 54 72 52 Z" fill="url(#cq-beak-grad)" stroke="#7c2d12" strokeWidth="0.9" strokeLinejoin="round" />
          <circle cx="80" cy="49" r="0.7" fill="#7c2d12" />
          <g className="duck-lower-beak">
            <path d="M74 50 Q84 50 82 55 Q77 56 73 54 Z" fill="#ea580c" stroke="#7c2d12" strokeWidth="0.9" strokeLinejoin="round" />
          </g>
          <circle cx="66" cy="41" r="3.4" fill="#ffffff" stroke="#0f172a" strokeWidth="0.6" />
          <circle cx="66.6" cy="41.3" r="2.2" fill="#0f172a" />
          <circle className="duck-eye-glint" cx="67.4" cy="40.3" r="0.9" fill="#ffffff" />
          <path d="M62 36 Q66 34 70 36" stroke="#7c2d12" strokeWidth="1.1" fill="none" strokeLinecap="round" />
        </g>
      </svg>
      {/* Duck-rig animations — co-located so anywhere this component mounts,
          the rigging animates identically. */}
      <style>{`
        .duck-svg {
          width: 100%; height: 100%;
          filter: drop-shadow(0 6px 10px rgba(0,0,0,0.35));
          animation: cq-body-bob 0.42s cubic-bezier(0.5,0,0.5,1) infinite;
          transform-origin: 50% 100%;
          will-change: transform;
          shape-rendering: geometricPrecision;
        }
        @keyframes cq-body-bob {
          0%   { transform: translateY(0)    scale(1.00, 1.00); }
          25%  { transform: translateY(-3px) scale(0.98, 1.03); }
          50%  { transform: translateY(0)    scale(1.02, 0.97); }
          75%  { transform: translateY(-3px) scale(0.98, 1.03); }
          100% { transform: translateY(0)    scale(1.00, 1.00); }
        }
        .duck-wing {
          transform-origin: 55px 62px;
          animation: cq-wing-flap 0.34s cubic-bezier(0.4,0,0.6,1) infinite;
          will-change: transform;
        }
        @keyframes cq-wing-flap {
          0%   { transform: rotate(-8deg) translateY(0); }
          40%  { transform: rotate(28deg) translateY(-1px); }
          70%  { transform: rotate(-4deg) translateY(0); }
          100% { transform: rotate(-8deg) translateY(0); }
        }
        .duck-wing-feather {
          transform-origin: 55px 62px;
          animation: cq-wing-feather 0.34s cubic-bezier(0.4,0,0.6,1) infinite;
          animation-delay: 0.06s;
          will-change: transform;
        }
        @keyframes cq-wing-feather {
          0%   { transform: rotate(-4deg); }
          40%  { transform: rotate(22deg); }
          70%  { transform: rotate(-2deg); }
          100% { transform: rotate(-4deg); }
        }
        .duck-head {
          transform-origin: 62px 44px;
          animation: cq-head-bob 0.42s cubic-bezier(0.5,0,0.5,1) infinite;
          will-change: transform;
        }
        @keyframes cq-head-bob {
          0%   { transform: rotate(0deg)  translate(0, 0); }
          25%  { transform: rotate(-3deg) translate(0.5px, -1px); }
          50%  { transform: rotate(1deg)  translate(0, 0); }
          75%  { transform: rotate(-2deg) translate(0.5px, -1px); }
          100% { transform: rotate(0deg)  translate(0, 0); }
        }
        .duck-lower-beak {
          transform-origin: 78px 50px;
          animation: cq-beak-open 1.2s cubic-bezier(0.5,0,0.5,1) infinite;
          will-change: transform;
        }
        @keyframes cq-beak-open {
          0%, 78%, 100% { transform: rotate(0deg); }
          82%           { transform: rotate(16deg); }
          88%           { transform: rotate(4deg); }
          92%           { transform: rotate(12deg); }
          96%           { transform: rotate(0deg); }
        }
        .duck-leg-front { transform-origin: 46px 78px; animation: cq-leg-front 0.42s cubic-bezier(0.5,0,0.5,1) infinite; will-change: transform; }
        .duck-leg-back  { transform-origin: 58px 78px; animation: cq-leg-back  0.42s cubic-bezier(0.5,0,0.5,1) infinite; will-change: transform; }
        @keyframes cq-leg-front { 0% { transform: rotate(-24deg); } 50% { transform: rotate(24deg); } 100% { transform: rotate(-24deg); } }
        @keyframes cq-leg-back  { 0% { transform: rotate(24deg); }  50% { transform: rotate(-24deg); } 100% { transform: rotate(24deg); } }
        .duck-tail { transform-origin: 30px 66px; animation: cq-tail-wag 0.42s cubic-bezier(0.5,0,0.5,1) infinite; will-change: transform; }
        @keyframes cq-tail-wag { 0% { transform: rotate(-6deg); } 50% { transform: rotate(8deg); } 100% { transform: rotate(-6deg); } }
        .duck-eye-glint { animation: cq-glint 3.2s ease-in-out infinite; }
        @keyframes cq-glint { 0%, 92%, 100% { opacity: 1; } 96% { opacity: 0.2; } }
      `}</style>
    </>
  );
}
